import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

// Assuming UserInfo type is correctly defined somewhere, e.g., '../../types'
interface UserInfo {
    token: string;
    // Add other properties if they are part of your UserInfo type, e.g., isAdmin, userId, etc.
}

const DepositPage: React.FC = () => {
    const location = useLocation();
    const navigate = useNavigate();

    // State to store user information from localStorage
    const [user, setUser] = useState<UserInfo | null>(null);

    // Fields for the deposit request form
    const [amount, setAmount] = useState<string>(location.state?.amount?.toString() || '');
    const [method, setMethod] = useState<string>(location.state?.paymentMethod || ''); // Pre-fill method if passed from HomePage
    const [transactionId, setTransactionId] = useState<string>(location.state?.transactionId || ''); // This is the bet's reference number
    const [receiptFile, setReceiptFile] = useState<File | null>(null);
    const [loading, setLoading] = useState(false);

    const isAmountPreFilled = !!location.state?.amount;
    const isTransactionIdPreFilled = !!location.state?.transactionId; // Refers to the bet's referenceNumber

    // Effect to retrieve user info from localStorage on component mount
    useEffect(() => {
        const userInfoString = localStorage.getItem('userInfo');
        if (userInfoString) {
            try {
                setUser(JSON.parse(userInfoString));
            } catch (e) {
                console.error("Failed to parse user info from localStorage:", e);
                setUser(null);
            }
        } else {
            toast.error('You must be logged in to make a deposit.');
        }

        // Set default payment method if coming from HomePage and it's provided
        if (location.state?.paymentMethod && !method) {
            setMethod(location.state.paymentMethod);
        }
    }, [location.state, method, navigate]);


    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!user || !user.token) {
            toast.error('You must be logged in to make a deposit.');
            return;
        }

        const parsedAmount = parseFloat(amount);
        if (isNaN(parsedAmount) || parsedAmount <= 0) {
            toast.error('Please enter a valid positive amount.');
            return;
        }

        if (!method) {
            toast.error('Please select a payment method.');
            return;
        }

        if (!transactionId || transactionId.trim() === '') {
            toast.error('Please enter the transaction ID (which is your bet reference number).');
            return;
        }

        if (!receiptFile) {
            toast.error('Please upload a payment receipt.');
            return;
        }

        const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif'];
        if (!allowedTypes.includes(receiptFile.type)) {
            toast.error('Only JPEG, JPG, PNG, GIF images are allowed for receipts.');
            setReceiptFile(null);
            return;
        }
        if (receiptFile.size > 5 * 1024 * 1024) { // 5MB limit
            toast.error('Receipt file size cannot exceed 5MB.');
            setReceiptFile(null);
            return;
        }

        setLoading(true);

        const depositFormData = new FormData();
        depositFormData.append('amount', amount);
        depositFormData.append('method', method);
        depositFormData.append('transactionId', transactionId); // This is the bet's reference number
        if (receiptFile) {
            depositFormData.append('receipt', receiptFile);
        }

        try {
            const depositResponse = await fetch(`${process.env.REACT_APP_API_BASE_URL}/payments/deposit`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${user.token}`, // Use token from directly fetched user
                },
                body: depositFormData,
            });

            const depositData = await depositResponse.json();

            if (!depositResponse.ok) {
                throw new Error(depositData.message || 'Deposit request failed.');
            }

            toast.success(depositData.message || 'Deposit request submitted successfully! Awaiting admin approval.');

            // Clear form fields after successful submission
            if (!isAmountPreFilled) setAmount('');
            if (!isTransactionIdPreFilled) setTransactionId('');
            setMethod('');
            setReceiptFile(null);

            // Navigate to a page showing bet status or dashboard
            navigate('/my-bets'); // User can check their bet status here
        } catch (error: any) {
            console.error('Deposit submission error:', error);
            toast.error(error.message || 'An error occurred during deposit submission.');
        } finally {
            setLoading(false);
        }
    };

    // Custom Tailwind animations via CSS (copied from HomePage for consistency)
    const customAnimations = `
        @keyframes fade-in-down {
            from { opacity: 0; transform: translateY(-20px); }
            to { opacity: 1; transform: translateY(0); }
        }
        @keyframes zoom-in-out {
            0% { transform: scale(0.9); opacity: 0; }
            50% { transform: scale(1.05); opacity: 1; }
            100% { transform: scale(1); }
        }
        @keyframes bounce-subtle {
            0%, 100% { transform: translateY(0); }
            50% { transform: translateY(-3px); }
        }
        .animate-fade-in-down {
            animation: fade-in-down 0.8s ease-out forwards;
        }
        .animate-zoom-in-out {
            animation: zoom-in-out 0.5s ease-out forwards;
        }
        .animate-bounce-subtle {
            animation: bounce-subtle 1.5s infinite ease-in-out;
        }
    `;

    return (
        <div className="container mx-auto p-4 md:p-8 min-h-screen flex items-center justify-center font-inter bg-gradient-to-br from-indigo-100 to-purple-200">
            {/* Inject custom CSS animations */}
            <style>{customAnimations}</style>

            <div className="flex flex-col lg:flex-row items-stretch justify-center gap-8 w-full max-w-5xl"> {/* Changed to flex container for horizontal layout */}
                {/* Important Deposit Information */}
                <div className="bg-white p-8 rounded-2xl shadow-3xl w-full lg:w-1/2 border-t-8 border-green-500 transform transition-all duration-500 hover:scale-[1.01] animate-zoom-in-out flex flex-col">
                    <h3 className="font-extrabold text-2xl mb-4 text-green-700 text-center">📌 Important Payment Information 📌</h3>
                    <p className="text-base text-gray-700 mb-4 text-center">
                        Follow these steps carefully to ensure your deposit is processed smoothly and your bet is confirmed!
                    </p>
                    <ul className="list-none text-base space-y-3 text-gray-700 flex-grow"> {/* flex-grow to ensure content fills space */}
                        <li className="flex items-start">
                            <span className="text-green-600 font-extrabold text-xl mr-3">1️⃣</span>
                            <div>
                                <span className="font-semibold">Select your preferred Payment Method.</span>
                                <p className="text-sm text-gray-500">Choose from the dropdown on the right.</p>
                            </div>
                        </li>
                        <li className="flex flex-col items-center justify-center py-4 border-b border-gray-200">
                            <span className="text-green-600 font-extrabold text-xl mb-2">2️⃣</span>
                            <span className="font-semibold text-center mb-3">Scan to Pay:</span>
                            {/* Placeholder for the actual QR Code image */}
                            <div className="p-2 border-4 border-green-400 rounded-lg bg-gray-50">
                                
                            </div>
                            <p className="text-sm text-gray-500 mt-3 text-center">Scan the QR code above using your payment app to transfer the exact amount.</p>
                        </li>
                        <li className="flex items-start mt-3">
                            <span className="text-green-600 font-extrabold text-xl mr-3">3️⃣</span>
                            <div>
                                <span className="font-semibold">Complete the transaction and save the receipt.</span> Take the screenshot or photo of the successful transaction.
                                <p className="text-sm text-gray-500 mt-1">This links your payment to your bet!</p>
                            </div>
                        </li>
                        <li className="flex items-start">
                            <span className="text-green-600 font-extrabold text-xl mr-3">4️⃣</span>
                            <div>
                                <span className="font-semibold">Enter the Transaction ID / Reference.</span>
                                <p className="text-sm text-gray-500 mt-1">This is the reference number provided by your payment system.</p>
                            </div>
                        </li>
                        <li className="flex items-start">
                            <span className="text-green-600 font-extrabold text-xl mr-3">5️⃣</span>
                            <div>
                                <span className="font-semibold">Upload a clear image of your payment receipt.</span>
                                <p className="text-sm text-gray-500 mt-1">Accepted formats: JPG, PNG, GIF (max 5MB).</p>
                            </div>
                        </li>
                        <li className="flex items-start">
                            <span className="text-green-600 font-extrabold text-xl mr-3">6️⃣</span>
                            <div>
                                <span className="font-semibold">Submit your deposit request!</span>
                                <p className="text-sm text-gray-500 mt-1">Deposits are typically processed within realtime after admin verification.</p>
                            </div>
                        </li>
                    </ul>
                </div>

                {/* Deposit Form */}
                <div className="bg-white p-8 rounded-2xl shadow-3xl w-full lg:w-1/2 border-t-8 border-purple-500 transform transition-all duration-500 hover:scale-[1.01] animate-zoom-in-out flex flex-col">
                    <h1 className="text-4xl font-extrabold text-center mb-8 text-purple-800 animate-fade-in-down">
                        💰 Deposit Details 💰 {/* Changed title for clarity */}
                    </h1>

                    {/* Conditional rendering for instructions based on whether it's a bet confirmation */}
                    {isTransactionIdPreFilled && ( // Check if transactionId (bet reference) is pre-filled
                        <p className="text-md text-blue-800 mb-6 text-center font-bold bg-gradient-to-r from-blue-100 to-blue-200 p-4 rounded-xl border border-blue-300 shadow-inner animate-bounce-subtle">
                            Please complete the payment for your bet reservation using Reference: <span className="font-extrabold text-blue-900 break-words">{transactionId || 'N/A'}</span>.
                        </p>
                    )}

                    <form onSubmit={handleSubmit} className="space-y-6 flex-grow flex flex-col justify-between"> {/* flex-grow to ensure content fills space */}
                        <div>
                            <label htmlFor="amount" className="block text-gray-700 text-lg font-bold mb-2">
                                Amount (Rs.)
                            </label>
                            <input
                                type="number"
                                id="amount"
                                value={amount}
                                onChange={(e) => setAmount(e.target.value)}
                                placeholder="e.g., 1000.00"
                                className="w-full px-5 py-3 border border-gray-300 rounded-lg focus:ring-purple-500 focus:border-purple-500 transition duration-150 ease-in-out text-lg shadow-sm"
                                required
                                min="1"
                                step="0.01"
                                disabled={isAmountPreFilled}
                            />
                             {isAmountPreFilled && <p className="text-xs text-blue-500 mt-2 font-medium">This amount is pre-filled from your bet reservation. Kindly pay this exact amount.</p>}
                        </div>

                        <div>
                            <label htmlFor="method" className="block text-gray-700 text-lg font-bold mb-2">
                                Payment Method
                            </label>
                            <select
                                id="method"
                                value={method}
                                onChange={(e) => setMethod(e.target.value)}
                                className="w-full px-5 py-3 border border-gray-300 rounded-lg focus:ring-purple-500 focus:border-purple-500 transition duration-150 ease-in-out text-lg shadow-sm bg-white"
                                required
                            >
                                <option value="">Select Method</option>
                                {/* Added general payment options, remove/modify as needed */}
                                <option value="Bank Transfer">Bank Transfer</option>
                                <option value="Mobile Money (QR)">Mobile Money (QR)</option>
                            </select>
                        </div>

                        <div>
                            <label htmlFor="transactionId" className="block text-gray-700 text-lg font-bold mb-2">
                                Transaction ID / Reference
                            </label>
                            <input
                                type="text"
                                id="transactionId"
                                value={transactionId}
                                onChange={(e) => setTransactionId(e.target.value)}
                                placeholder="Enter transaction ID from your payment"
                                className="w-full px-5 py-3 border border-gray-300 rounded-lg focus:ring-purple-500 focus:border-purple-500 transition duration-150 ease-in-out text-lg shadow-sm"
                                required
                                disabled={isTransactionIdPreFilled}
                            />
                            {isTransactionIdPreFilled && <p className="text-xs text-blue-500 mt-2 font-medium">This reference number is from your bet reservation. Use it as your transaction ID.</p>}
                        </div>

                        <div>
                            <label htmlFor="receipt" className="block text-gray-700 text-lg font-bold mb-2">
                                Upload Payment Receipt (Image: JPG, PNG, GIF)
                            </label>
                            <input
                                type="file"
                                id="receipt"
                                accept="image/jpeg,image/jpg,image/png,image/gif"
                                onChange={(e) => setReceiptFile(e.target.files ? e.target.files[0] : null)}
                                className="w-full text-lg file:mr-4 file:py-2 file:px-4
                                           file:rounded-full file:border-0 file:text-base file:font-semibold
                                           file:bg-purple-100 file:text-purple-700 hover:file:bg-purple-200 transition duration-200 ease-in-out cursor-pointer"
                                required
                            />
                             {receiptFile && <p className="text-md text-gray-600 mt-2 font-medium">Selected: <span className="font-semibold text-purple-700">{receiptFile.name}</span> (Size: {(receiptFile.size / 1024 / 1024).toFixed(2)} MB)</p>}
                        </div>

                        <button
                            type="submit"
                            className="w-full bg-gradient-to-r from-purple-600 to-indigo-700 hover:from-purple-700 hover:to-indigo-800 text-white font-extrabold py-3 px-6 rounded-lg transition duration-300 ease-in-out shadow-lg text-xl transform hover:scale-105 animate-bounce-subtle
                                disabled:from-gray-400 disabled:to-gray-500 disabled:text-gray-200 disabled:shadow-none disabled:cursor-not-allowed"
                            disabled={loading || !receiptFile}
                        >
                            {loading ? 'Submitting Request...' : 'Submit Deposit Request'}
                        </button>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default DepositPage;